import { actionTypes } from '../actions/types';
import { TrafficLight } from '../generated-types';
import { VehicleReducer } from './vehicle';

type AppActions = import('../actions/types').AppActions;

export interface CompletionReducer {
    // Ongoing Application
    ongoing: {
        email: string | undefined;
        phone: string | undefined;
        applicationId: string | undefined;
        applicationStatus: string | undefined;
        esigningStatus: string | undefined;
        esigningUrl: string | undefined;
        signingOrderId: string | undefined;
        hasInsurance: boolean | undefined;
        eligibleForInsurance: boolean | undefined;
        orderInsurance: boolean;
        loanCommitmentFileData: string | null;
        missingVariables: string[] | null;
        vehicleDetails?: VehicleReducer['vehicleDetails'] | null;
        signedByCurrentUser: boolean;
        debtDocSigned: boolean;
        customerIsMainApplicant: boolean;
        trafficLight?: TrafficLight;
        advisorApplication?: boolean;
    };
}

export const initialState: CompletionReducer = {
    // Ongoing Application
    ongoing: {
        email: undefined,
        phone: undefined,
        applicationId: undefined,
        applicationStatus: undefined,
        esigningStatus: undefined,
        eligibleForInsurance: undefined,
        hasInsurance: undefined,
        orderInsurance: false,
        esigningUrl: undefined,
        signingOrderId: undefined,
        loanCommitmentFileData: null,
        missingVariables: null,
        vehicleDetails: null,
        signedByCurrentUser: false,
        debtDocSigned: false,
        customerIsMainApplicant: true,
        trafficLight: undefined,
        advisorApplication: false,
    },
};

export const completionReducer = (state = initialState, action: AppActions = {} as AppActions): CompletionReducer => {
    switch (action.type) {
        case actionTypes.FETCH_USER_CONTEXT_FULFILLED:
        case actionTypes.FORM_COMPLETION_DETAILS: {
            const { email, phone } = action.payload;
            return {
                ...state,
                ongoing: {
                    ...state.ongoing,
                    email: email || state.ongoing.email,
                    phone: phone || state.ongoing.phone,
                },
            };
        }

        case actionTypes.SEND_APPLICATION_FULFILLED:
        case actionTypes.FETCH_APPLICATION_FULFILLED:
        case actionTypes.UPDATE_APPLICATION_FULFILLED: {
            const parseSigningOrderId = (esigningOrderId: unknown): string | undefined => {
                if (esigningOrderId === null || esigningOrderId === undefined) {
                    return undefined;
                }

                const asString = String(esigningOrderId).trim();
                return /^\d+$/.test(asString) ? asString : undefined;
            };

            const applicationId = action.payload?.applicationId;
            const applicationStatus = action.payload?.applicationStatus;
            const esigningStatus = action.payload?.esigningStatus;
            const esigningUrl = action.payload?.esigningUrl || '';
            const signingOrderId = parseSigningOrderId(action.payload?.esigningOrderId);
            const loanCommitmentFileData = action.payload?.loanCommitmentFileData || '';
            const missingVariables = action.payload?.missingVariables || [];
            const vehicleDetails = action.payload?.vehicleDetails || null;
            const signedByCurrentUser = action.payload?.signedByCurrentUser || false;
            const debtDocSigned = action.payload?.debtDocSigned || false;
            const customerIsMainApplicant = action.payload?.customerIsMainApplicant || false;
            const trafficLight = action.payload?.trafficLight || undefined;
            const advisorApplication = action.payload?.advisorApplication || undefined;
            const eligibleForInsurance = action.payload?.eligibleForInsurance || undefined;
            const hasInsurance = action.payload?.hasInsurance || undefined;

            return {
                ...state,
                ongoing: {
                    ...state.ongoing,
                    applicationId,
                    applicationStatus,
                    esigningStatus,
                    esigningUrl,
                    signingOrderId,
                    eligibleForInsurance,
                    hasInsurance,
                    loanCommitmentFileData,
                    missingVariables,
                    vehicleDetails,
                    signedByCurrentUser,
                    debtDocSigned,
                    customerIsMainApplicant,
                    trafficLight,
                    advisorApplication,
                },
            };
        }

        default:
            return state;
    }
};
